import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid, withDefaults } from './common.js';
import { FieldValueOut } from './fields.js';
/**
 * Members of repeat groups and member import/export (module `members`, docs/modules/groups-members.md). Owner: task S2.
 */
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const MemberStatus = z.enum(['current', 'archived']);
export const Member = z.object({
    id: Uuid,
    groupId: Uuid,
    fullName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** Job role / title (built-in personal field "Role"). */
    role: z.string().nullable(),
    /** Your own reference, used to match rows on import. */
    externalId: z.string().nullable(),
    photoFileId: Uuid.nullable(),
    photoThumbFileId: Uuid.nullable(),
    status: MemberStatus,
    startDate: DateOnly.nullable(),
    endDate: DateOnly.nullable(),
    hasPin: z.boolean(),
    /** Custom personal field values, in field order. */
    fields: z.array(FieldValueOut),
    lastVisitAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
const MemberBase = z.object({
    fullName: z.string().trim().min(1).max(120),
    email: z.union([z.literal(''), z.email()]).nullable().optional(),
    phone: z.string().trim().max(32).nullable().optional(),
    role: z.string().trim().max(80).nullable().optional(),
    externalId: z.string().trim().max(100).nullable().optional(),
    startDate: DateOnly.nullable().optional(),
    endDate: DateOnly.nullable().optional(),
    /** fieldId -> value. Only personal fields of this member's group. Sensitive ones need groups.manage_sensitive. */
    fields: z.record(z.string(), z.unknown()).optional(),
});
export const MemberCreate = MemberBase.extend({ groupId: Uuid });
export const MemberUpdate = MemberBase.partial().extend({ status: MemberStatus.optional() });
export const MemberListQuery = PageQuery.extend({
    groupId: Uuid,
    status: z.enum(['current', 'archived', 'all']).default('current'),
});
/** POST /members/bulk */
export const MemberBulkAction = z.object({
    groupId: Uuid,
    memberIds: z.array(Uuid).min(1).max(1000),
    action: z.discriminatedUnion('type', [
        z.object({ type: z.literal('archive') }),
        z.object({ type: z.literal('restore') }),
        z.object({ type: z.literal('delete') }),
        z.object({ type: z.literal('set_field'), fieldId: Uuid, value: z.unknown() }),
    ]),
});
export const MemberBulkResult = z.object({ updated: z.number().int(), skipped: z.number().int() });
/** PUT /members/:id/pin. null removes it. */
export const MemberPinSet = z.object({ pin: z.string().regex(/^\d{4}$/).nullable() });
export const MemberExportQuery = z.object({
    groupId: Uuid,
    format: z.enum(['csv', 'xlsx']).default('csv'),
    status: z.enum(['current', 'archived', 'all']).default('current'),
});
// ---------- Import (CSV and Excel, with column mapping and preview) ----------
/** Where an import column goes. `field:<uuid>` = a custom personal field. */
export const ImportTarget = z.union([
    z.enum(['ignore', 'fullName', 'firstName', 'lastName', 'email', 'phone', 'role', 'externalId', 'startDate', 'endDate', 'status']),
    z.string().regex(/^field:[0-9a-f-]{36}$/),
]);
export const MemberImportStatus = z.enum(['uploaded', 'validated', 'queued', 'running', 'done', 'failed', 'cancelled']);
/** Response to the upload (POST /members/imports, multipart: groupId + file). */
export const MemberImportUploaded = z.object({
    importId: Uuid,
    fileName: z.string(),
    /** Header row (or "Column 1", "Column 2" ... when hasHeader is false). */
    columns: z.array(z.string()),
    /** First 20 data rows as text. */
    sampleRows: z.array(z.array(z.string())),
    rowCount: z.number().int(),
    /** Same length as columns; best guess from header names. */
    suggestedMapping: z.array(ImportTarget),
});
/** POST /members/imports/:id/validate */
export const MemberImportMapping = z.object({
    hasHeader: z.boolean().default(true),
    /** One target per column. */
    mapping: z.array(ImportTarget).min(1).max(100),
    matchBy: z.enum(['email', 'externalId', 'fullName', 'none']).default('email'),
    updateExisting: z.boolean().default(true),
    /** Archive current members not found in the file. */
    archiveMissing: z.boolean().default(false),
    dateFormat: z.enum(['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']).default('DD/MM/YYYY'),
});
export const ImportRowError = z.object({
    /** 1-based row number as the person sees it in their spreadsheet. */
    row: z.number().int(),
    column: z.string().nullable(),
    messageKey: z.string(),
    params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
/** Preview before saving, and the final result (same shape). */
export const MemberImportSummary = z.object({
    importId: Uuid,
    status: MemberImportStatus,
    toCreate: z.number().int(),
    toUpdate: z.number().int(),
    toArchive: z.number().int(),
    unchanged: z.number().int(),
    errorCount: z.number().int(),
    /** First 200 problems. Rows with errors are skipped on commit. */
    errors: z.array(ImportRowError),
    /** Up to 20 rows as they will be saved, for the preview table. */
    preview: z.array(z.object({ row: z.number().int(), action: z.enum(['create', 'update', 'archive', 'unchanged', 'error']), fullName: z.string(), email: z.string().nullable() })),
    finishedAt: IsoDateTime.nullable(),
});
export const MembersTenantSettings = z.object({
    import: z
        .object({
        /** Largest file accepted (MB). */
        maxFileMb: z.number().int().min(1).max(20).default(5),
        maxRows: z.number().int().min(1).max(50000).default(10000),
    })
        .default({ maxFileMb: 5, maxRows: 10000 }),
});
/** Group-level settings of the `members` module (repeat groups). 0 = off. */
export const MembersGroupSettings = z.object({
    lifecycle: z
        .object({
        /** Archive members with no visit for this many days. */
        archiveInactiveAfterDays: z.number().int().min(0).max(3650).default(0),
        /** Delete archived members after this many days archived (their visits stay, by visit retention). */
        deleteArchivedAfterDays: z.number().int().min(0).max(3650).default(0),
    })
        .default({ archiveInactiveAfterDays: 0, deleteArchivedAfterDays: 0 }),
    // ---- Phase 2 (task S16, docs/modules/members-phase2.md): onboarding email (spec 2.4) ----
    onboarding: withDefaults(z.object({
        enabled: z.boolean().default(false),
        /** 'created' = when the member is added; 'start_date' = 08:00 site-local on their start date. */
        sendOn: z.enum(['created', 'start_date']).default('created'),
        subject: z.string().max(200).default(''),
        body: z.string().max(20000).default(''),
        attachmentFileId: Uuid.nullable().default(null),
        replyTo: z.string().max(254).nullable().default(null),
        includeQr: z.boolean().default(true),
        /** Add a Companion app invite link when the group has the app on. */
        includeCompanionInvite: z.boolean().default(false),
    })),
});
// ---------- Kiosk (device) view ----------
export const KioskMember = z.object({
    id: Uuid,
    groupId: Uuid,
    /** Formatted by the group's nameDisplay setting. */
    displayName: z.string(),
    /** Lower-case search text (full name), for the kiosk's local search. */
    searchName: z.string(),
    /** Signed, short-lived link; the kiosk caches the image by photoThumbFileId. */
    photoThumbFileId: Uuid.nullable(),
    photoThumbUrl: z.string().nullable(),
    /** ADR 0008 "PINs on the kiosk". */
    pinHash: z.string().nullable(),
    /** Starred non-sensitive personal fields, as text. */
    starred: z.array(z.object({ label: z.string(), text: z.string() })).max(4),
    /** Phase 2: current member QR version (CS1.M codes with an older version are refused). */
    qrVersion: z.number().int().optional(),
    updatedAt: IsoDateTime,
});
/** GET /members/device/delta?cursor= : current members of groups visible at the device's site. */
export const KioskMemberDelta = z.object({
    items: z.array(KioskMember),
    /** Members archived, deleted or no longer visible since the cursor. */
    removedIds: z.array(Uuid),
    cursor: z.string(),
    hasMore: z.boolean(),
    /** True when the server restarted the list (visible groups changed): the kiosk replaces its members with this and later pages. */
    reset: z.boolean(),
});
// ---------- Phase 2 (task S16, docs/modules/members-phase2.md) ----------
/** Kiosk config section `members`: the tenant's member QR key, so kiosks check CS1.M codes offline. */
export const MembersKioskSection = z.object({ tenantId: Uuid, memberQrKey: z.string() });
/** POST /members/:id/qr/regenerate -> the new code (old printed or emailed codes stop working). */
export const MemberQr = z.object({ memberId: Uuid, qrVersion: z.number().int(), qrPayload: z.string() });
/** POST /members/:id/onboarding/send -> queued or why not. */
export const OnboardingSendResult = z.object({ queued: z.boolean(), reasonKey: z.string().nullable() });
/** POST /members/onboarding-attachment -> the stored file to name in `onboarding.attachmentFileId`. */
export const OnboardingAttachmentResult = z.object({ fileId: Uuid });
//# sourceMappingURL=members.js.map